// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { getIsShareEntity } from "@insite/client-framework/ServerSideRendering";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { XNGAGE_ELEMENTS } from "@steiner/XDL/constants";
import { getScripts } from "@steiner/XDL/Utilities";
import React, { useEffect } from "react";

const enum fields {
    region = "region",
    portalId = "portalId",
    formId = "formId",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.region]: string;
        [fields.portalId]: string;
        [fields.formId]: string;
    };
}

type Props = OwnProps;

interface HubSpotFormScriptWidgetStyles {
    container?: InjectableCss;
    title?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
}

export const hubSpotFormScriptWidgetStyles: HubSpotFormScriptWidgetStyles = {};

const styles = hubSpotFormScriptWidgetStyles;
const HubSpotFormScriptWidget = ({ fields: { region, portalId, formId }, id }: Props) => {
    const { isInShell } = useShellContext();
    // ensured id starts with letters because widget id starts with numbers
    // and it's not valid for div id to start with numbers
    const containerId = `hubspot-${id}`;

    const initHubSpotScript = () => {
        if (!region || !portalId || !formId || !id || !containerId || getIsShareEntity()) {
            return;
        }
        const scriptId = `hsforms_${id}`;

        if (document.getElementById(scriptId)) {
            return;
        }

        getScripts
            .url("https://js.hsforms.net/forms/embed/v2.js", {
                appendTo: document.getElementById(containerId) || undefined,
                async: true,
                id: scriptId,
            })
            .then(() => {
                try {
                    (window as any).hbspt.forms.create({
                        region: `${region}`,
                        portalId: `${portalId}`,
                        formId: `${formId}`,
                        target: `#${containerId}`,
                    });
                } catch (e) {
                    throw new Error(e);
                }
            });
    };

    useEffect(() => {
        if (!isInShell) {
            return;
        }

        // It's editor mode
        initHubSpotScript();
    }, [region, portalId, formId]);

    useEffect(() => {
        if (isInShell) {
            return;
        }

        initHubSpotScript();
    }, []);

    if (isInShell) {
        return (
            <StyledWrapper {...styles.container} id={containerId} data-test-selectore="HubSpotFormScriptWidget">
                <Typography {...styles.title}>{translate("HubSpot Form Script")}</Typography>
                <Typography {...styles.description}>{translate("Execute HubSpot Form Script")}</Typography>
            </StyledWrapper>
        );
    }

    if (getIsShareEntity()) {
        return null;
    }

    return <StyledWrapper id={containerId}></StyledWrapper>;
};

const widgetModule: WidgetModule = {
    component: HubSpotFormScriptWidget,
    definition: {
        group: XNGAGE_ELEMENTS,
        fieldDefinitions: [
            {
                name: fields.region,
                displayName: "Region",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.portalId,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.formId,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
